import React, { useState, useEffect, useRef } from 'react';
import StarRating from './StarRating';
import { getProductPlaceholder } from '../utils/placeholder';

function ProductCard({ product, onAddToCart, onQuickView, onToggleWishlist, isWishlisted: propIsWishlisted }) {
  const [isWishlisted, setIsWishlisted] = useState(propIsWishlisted || false);
  const addToCartButtonRef = useRef(null);
  
  // Sync with prop changes
  useEffect(() => {
    if (propIsWishlisted !== undefined) {
      setIsWishlisted(propIsWishlisted);
    }
  }, [propIsWishlisted]);

  const formatSold = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K+`;
    }
    return `${count}+`;
  };

  // Handle both API data structure and sample data
  const displayPrice = parseFloat(product.price) || 0;
  const originalPrice = product.original_price ? parseFloat(product.original_price) : (product.originalPrice ? parseFloat(product.originalPrice) : null);
  const sold = product.sold || 0; // Use actual sold count from API (calculated from order_items)
  const rating = product.rating || 4.5; // Default rating if not provided
  const reviews = product.reviews || 0;
  
  // Check if product has colors and sizes
  const hasColors = product.colors && Array.isArray(product.colors) && product.colors.length > 0;
  const hasSizes = product.sizes && Array.isArray(product.sizes) && product.sizes.length > 0;
  
  // Calculate display quantity (total sold across all variants):
  // Priority: variant_sold (color-size) > color_sold (color only) > general sold
  const displayQuantity = (() => {
    // If product has both colors and sizes, check variant_sold first
    if (hasColors && hasSizes && product.variant_sold && typeof product.variant_sold === 'object') {
      const totalVariantSold = Object.values(product.variant_sold).reduce((sum, soldQty) => {
        const soldNum = parseInt(soldQty) || 0;
        return sum + soldNum;
      }, 0);
      if (totalVariantSold > 0) {
        return totalVariantSold;
      }
    }
    
    // If product has colors (with or without sizes), check color_sold
    if (hasColors && product.color_sold && typeof product.color_sold === 'object') {
      const totalColorSold = Object.values(product.color_sold).reduce((sum, soldQty) => {
        const soldNum = parseInt(soldQty) || 0;
        return sum + soldNum;
      }, 0);
      if (totalColorSold > 0) {
        return totalColorSold;
      }
    }
    
    // Fallback to general sold for non-color products
    return sold;
  })();
  
  // Determine badges - check if product has sale (originalPrice), is new, or has promo
  const hasSale = originalPrice && originalPrice > displayPrice;
  // Calculate percentage discount
  const discountPercentage = hasSale 
    ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
    : 0;
  // Check is_new field from API (handle boolean, number, or string)
  // Only show "New" badge if explicitly marked as new (checkbox checked)
  const isNew = Boolean(product.is_new);
  // Check is_promotional field from API (handle boolean, number, or string)
  const hasPromo = Boolean(product.is_promotional);
  
  // Use placeholder if image is missing or empty
  const placeholderImage = getProductPlaceholder(product.name, 500, 500);
  const imageUrl = product.image && product.image.trim() !== '' 
    ? product.image 
    : placeholderImage;

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
    if (onToggleWishlist) {
      onToggleWishlist(product, !isWishlisted);
    }
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    if (onQuickView) {
      onQuickView(product);
    }
  };

  // Helper function to find the first available variant with stock
  const findFirstAvailableVariant = () => {
    if (!hasColors && !hasSizes) {
      // No variants, check general stock
      const stock = parseInt(product.stock) || 0;
      const sold = parseInt(product.sold) || 0;
      return stock - sold > 0 ? { color: null, size: null, hasStock: true } : { hasStock: false };
    }

    // Check variant_stock (color-size combinations) first
    if (hasColors && hasSizes && product.variant_stock && typeof product.variant_stock === 'object') {
      const colors = product.colors || [];
      const sizes = product.sizes || [];
      for (const color of colors) {
        for (const size of sizes) {
          const variantKey = `${color}-${size}`;
          const variantStock = parseInt(product.variant_stock[variantKey]) || 0;
          const variantSold = parseInt(product.variant_sold?.[variantKey]) || 0;
          if (variantStock - variantSold > 0) {
            return { color, size, hasStock: true };
          }
        }
      }
    }

    // Check color_stock if no variant stock found
    if (hasColors && product.color_stock && typeof product.color_stock === 'object') {
      const colors = product.colors || [];
      for (const color of colors) {
        const colorStock = parseInt(product.color_stock[color]) || 0;
        const colorSold = parseInt(product.color_sold?.[color]) || 0;
        if (colorStock - colorSold > 0) {
          return { color, size: null, hasStock: true };
        }
      }
    }

    // No available stock found
    return { hasStock: false };
  };

  const handleAddToCartClick = (e) => {
    e.stopPropagation();
    
    // If product has variants, check for available stock
    if (hasColors || hasSizes) {
      const availableVariant = findFirstAvailableVariant();
      
      if (availableVariant.hasStock) {
        // Add to cart with the first available variant
        const productWithVariant = {
          ...product,
          selectedColor: availableVariant.color,
          selectedSize: availableVariant.size,
          quantity: 1
        };
        
        if (onAddToCart && addToCartButtonRef.current) {
          const rect = addToCartButtonRef.current.getBoundingClientRect();
          const buttonCenterX = rect.left + rect.width / 2;
          const buttonCenterY = rect.top + rect.height / 2;
          onAddToCart(productWithVariant, { x: buttonCenterX, y: buttonCenterY });
        }
      } else {
        // No stock available, open QuickViewModal so user can see all options
        if (onQuickView) {
          onQuickView(product);
        }
      }
    } else {
      // No variants, add directly to cart
      if (onAddToCart && addToCartButtonRef.current) {
        const rect = addToCartButtonRef.current.getBoundingClientRect();
        const buttonCenterX = rect.left + rect.width / 2;
        const buttonCenterY = rect.top + rect.height / 2;
        onAddToCart(product, { x: buttonCenterX, y: buttonCenterY });
      }
    }
  };

  return (
    <div className="group bg-white rounded-lg overflow-hidden shadow-md transition-all duration-200 cursor-pointer flex flex-col hover:-translate-y-1 hover:shadow-xl relative">
      <div className="w-full aspect-[3/4] overflow-hidden bg-gray-100 flex items-center justify-center relative">
        <img 
          src={imageUrl} 
          alt={product.name} 
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                onError={(e) => {
                  e.target.src = getProductPlaceholder(product.name, 400, 400);
                }}
        />
        
        {/* Feature Badges - Left Side */}
        {(isNew || hasPromo) && (
          <div className="absolute top-2 sm:top-2.5 left-2 sm:left-2.5 flex flex-col gap-1.5 sm:gap-2 z-20">
            {isNew && (
              <span className="bg-green-500 text-white text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-lg">
                New
              </span>
            )}
            {hasPromo && (
              <span className="bg-primary text-white text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-lg">
                Promo
              </span>
            )}
          </div>
        )}

        {/* Action Buttons - Center - Only show on hover */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-row gap-2 sm:gap-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={handleWishlistToggle}
            className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 backdrop-blur-sm ${
              isWishlisted 
                ? 'bg-primary/90 text-white' 
                : 'bg-white/90 text-primary'
            }`}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" fill={isWishlisted ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
          <button
            onClick={handleQuickView}
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-white/90 text-gray-700 flex items-center justify-center shadow-lg transition-all hover:scale-110 hover:bg-white backdrop-blur-sm"
            aria-label="Quick view"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      </div>
      <div className="p-1 sm:p-1.5 md:p-2 flex flex-col gap-0.5 sm:gap-1 flex-1 relative">
        <h3 className="text-[10px] sm:text-[11px] md:text-xs font-medium text-gray-800 m-0 leading-tight line-clamp-1 truncate">
          {product.name}
        </h3>
        <div className="flex flex-col gap-0.5 sm:gap-1 mt-0.5">
          {hasSale ? (
            <>
              <div className="flex items-baseline gap-1 sm:gap-1.5 flex-wrap">
                <span className="text-xs sm:text-sm md:text-base font-bold text-primary">GH¢{displayPrice.toFixed(2)}</span>
                <span className="text-[9px] sm:text-[10px] text-gray-500 line-through">GH¢{originalPrice.toFixed(2)}</span>
                <span className="text-[9px] sm:text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                  -{discountPercentage}%
                </span>
              </div>
              <div className="flex items-center gap-1 text-[8px] sm:text-[9px] text-gray-600 pr-10 sm:pr-12 md:pr-14">
                {hasColors ? (
                  displayQuantity > 0 && <span>Sold: {displayQuantity}</span>
                ) : (
                  displayQuantity > 0 && <span>{formatSold(displayQuantity)} sold</span>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-baseline gap-0.5 sm:gap-1 flex-wrap pr-10 sm:pr-12 md:pr-14">
              <span className="text-xs sm:text-sm md:text-base font-bold text-primary">GH¢{displayPrice.toFixed(2)}</span>
              <div className="flex items-center gap-1 text-[8px] sm:text-[9px] text-gray-600 ml-auto">
                {hasColors ? (
                  displayQuantity > 0 && <span>Sold: {displayQuantity}</span>
                ) : (
                  displayQuantity > 0 && <span>{formatSold(displayQuantity)} sold</span>
                )}
              </div>
            </div>
          )}
        </div>
        {(rating > 0 || reviews > 0) && (
          <div className="flex items-center gap-0.5 mt-0.5">
            {rating > 0 && <StarRating rating={rating} />}
            {reviews > 0 && (
              <span className="text-[8px] sm:text-[9px] text-gray-600">({reviews})</span>
            )}
          </div>
        )}
        <button 
          ref={addToCartButtonRef}
          className="absolute bottom-1 sm:bottom-1.5 md:bottom-2 right-1 sm:right-1.5 md:right-2 w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full bg-secondary text-white border-0 cursor-pointer flex items-center justify-center shadow-lg transition-all hover:scale-110 hover:shadow-xl hover:bg-secondary-dark"
          onClick={handleAddToCartClick}
          aria-label={`Add ${product.name} to cart`}
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

export default ProductCard;
